import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Link2, Loader2, Info } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PRIORITIES = ["cok_istiyorum", "istiyorum", "olursa_guzel"];

const empty = {
  name: "", description: "", image_url: "", price: "", store: "", product_url: "", currency: "TRY",
  size: "", color: "", quantity: 1, note: "", priority: "istiyorum", is_group_gift: false, target_amount: "",
};

export const AddItemDialog = ({ open, onOpenChange, wishlistId, editItem, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(empty);
  const [linkUrl, setLinkUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("link");
  const [fetchStatus, setFetchStatus] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(editItem ? { ...empty, ...editItem, price: editItem.price ?? "", target_amount: editItem.target_amount ?? "" } : empty);
      setTab(editItem ? "manual" : "link");
      setLinkUrl("");
      setFetchStatus(null);
    }
  }, [open, editItem]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchLink = async () => {
    if (!linkUrl.trim()) return;
    setFetching(true);
    try {
      const res = await api.post("/utils/fetch-link-metadata", { url: linkUrl.trim() });
      const d = res.data;
      setForm((f) => ({ ...f, name: d.name || f.name, price: d.price ?? f.price, store: d.store || f.store,
        currency: d.currency || f.currency || "TRY", product_url: d.product_url }));
      setTab("manual");
      const full = d.success && d.price != null;
      setFetchStatus(full ? "full" : "fail");
      toast[full ? "success" : "info"](full ? t("item_fetched") : t("link_read_fail"));
    } catch {
      toast.error(t("link_read_fail"));
      setForm((f) => ({ ...f, product_url: linkUrl.trim(), store: f.store }));
      setFetchStatus("fail");
      setTab("manual");
    } finally { setFetching(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("item_name_req")); return; }
    setBusy(true);
    const payload = {
      ...form,
      price: form.price === "" ? null : parseFloat(form.price),
      target_amount: form.target_amount === "" ? null : parseFloat(form.target_amount),
      quantity: parseInt(form.quantity) || 1,
    };
    try {
      if (editItem) {
        await api.put(`/items/${editItem.item_id}`, payload);
        toast.success(t("item_updated"));
      } else {
        await api.post(`/wishlists/${wishlistId}/items`, payload);
        toast.success(t("item_added"));
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{editItem ? t("edit_item") : t("add_item")}</DialogTitle>
          <DialogDescription className="sr-only">{t("link_desc")}</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          {!editItem && (
            <TabsList className="grid grid-cols-2 rounded-full w-full">
              <TabsTrigger value="link" className="rounded-full" data-testid="additem-tab-link">{t("tab_link")}</TabsTrigger>
              <TabsTrigger value="manual" className="rounded-full" data-testid="additem-tab-manual">{t("tab_manual")}</TabsTrigger>
            </TabsList>
          )}
          <TabsContent value="link" className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">{t("link_desc")}</p>
            <div className="flex gap-2">
              <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://www.trendyol.com/..."
                className="rounded-xl" data-testid="additem-link-input" />
              <Button onClick={fetchLink} disabled={fetching} className="rounded-full shrink-0" data-testid="additem-fetch-btn">
                {fetching ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />}
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="manual" className="space-y-4 mt-4">
            {(fetchStatus === "fail" || fetchStatus === "partial") && (
              <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-xl p-3 text-sm" data-testid="additem-fetch-fallback-msg">
                <Info size={16} className="mt-0.5 shrink-0" />
                <span>{t("link_read_fail")}</span>
              </div>
            )}
            <div>
              <Label>{t("lbl_item_name")}</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 rounded-xl" placeholder="Nike Air Max" data-testid="additem-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("lbl_price")}</Label>
                <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1 rounded-xl" placeholder="5499" data-testid="additem-price-input" />
              </div>
              <div>
                <Label>{t("lbl_store")}</Label>
                <Input value={form.store} onChange={(e) => set("store", e.target.value)} className="mt-1 rounded-xl" placeholder="Trendyol" data-testid="additem-store-input" />
              </div>
            </div>
            <div>
              <Label>{t("lbl_product_url")}</Label>
              <Input value={form.product_url} onChange={(e) => set("product_url", e.target.value)} className="mt-1 rounded-xl" placeholder="https://..." data-testid="additem-url-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>{t("lbl_size")}</Label>
                <Input value={form.size} onChange={(e) => set("size", e.target.value)} className="mt-1 rounded-xl" placeholder="42" />
              </div>
              <div>
                <Label>{t("lbl_color")}</Label>
                <Input value={form.color} onChange={(e) => set("color", e.target.value)} className="mt-1 rounded-xl" placeholder={t("color_ph")} />
              </div>
              <div>
                <Label>{t("lbl_qty")}</Label>
                <Input type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="mt-1 rounded-xl" />
              </div>
            </div>
            <div>
              <Label>{t("lbl_priority")}</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="additem-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((k) => <SelectItem key={k} value={k}>{t(`prio_${k}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("lbl_note")}</Label>
              <Textarea value={form.note} onChange={(e) => set("note", e.target.value)} className="mt-1 rounded-xl" placeholder={t("note_ph")} rows={2} />
            </div>
            <div className="flex items-center justify-between bg-secondary/60 rounded-xl p-4">
              <div>
                <Label>{t("group_gift")}</Label>
                <p className="text-xs text-muted-foreground">{t("group_gift_d")}</p>
              </div>
              <Switch checked={form.is_group_gift} onCheckedChange={(v) => set("is_group_gift", v)} data-testid="additem-groupgift-switch" />
            </div>
            {form.is_group_gift && (
              <div>
                <Label>{t("lbl_target")}</Label>
                <Input type="number" value={form.target_amount} onChange={(e) => set("target_amount", e.target.value)} className="mt-1 rounded-xl" placeholder="30000" data-testid="additem-target-input" />
              </div>
            )}
            <Button onClick={save} disabled={busy} className="w-full rounded-full h-11 font-semibold" data-testid="additem-save-btn">
              {busy ? t("saving") : editItem ? t("update_btn") : t("add_to_list")}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
